import { Show, useRecordContext, useGetList, EditButton } from 'react-admin';
import {
  Box, Typography, Card, CardContent, Stack, Divider,
  Table, TableHead, TableRow, TableCell, TableBody,
  Chip, Button,
} from '@mui/material';
import PhoneIcon     from '@mui/icons-material/Phone';
import EmailIcon     from '@mui/icons-material/Email';
import NoteIcon      from '@mui/icons-material/Notes';
import AddIcon       from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import { card, tbl, NAVY, GOLD, btn, fmtQty, typo } from '../styles/ds';

const fmtDate = iso => iso ? new Date(iso).toLocaleDateString('he-IL') : '—';
const fmtCost = (amount, currency) => currency === 'USD'
  ? `$${Number(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`
  : `₪${Number(amount || 0).toLocaleString('he-IL', { minimumFractionDigits: 2 })}`;

const SupplierDetail = () => {
  const record = useRecordContext();

  const { data: purchases = [], isLoading } = useGetList('purchases', {
    filter: { supplier_id: record?.id },
    pagination: { page: 1, perPage: 500 },
    sort: { field: 'purchase_date', order: 'DESC' },
  }, { enabled: !!record?.id });

  const navigate = useNavigate();
  if (!record) return null;

  // Summary totals
  const totalIls = purchases
    .filter(p => p.currency === 'ILS')
    .reduce((s, p) => s + Number(p.unit_cost || 0) * Number(p.quantity || 1), 0);
  const totalUsd = purchases
    .filter(p => p.currency === 'USD')
    .reduce((s, p) => s + Number(p.unit_cost || 0) * Number(p.quantity || 1), 0);
  const totalItems = purchases.reduce((s, p) => s + Number(p.quantity || 1), 0);

  return (
    <Box className="crm-page crm-detail-content" p={3}>
      {/* Header */}
      <Stack className="crm-header" direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography {...typo.pageTitle}>{record.name}</Typography>
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained" size="small" startIcon={<AddIcon />}
            onClick={() => navigate('/purchases/create', { state: { supplier_id: record.id, supplier_name: record.name } })}
            sx={btn.primary}
          >
            הוסף רכישה
          </Button>
          <EditButton label="ערוך ספק" />
        </Stack>
      </Stack>

      {/* Contact info */}
      <Card sx={card.section}>
        <CardContent className="crm-card-content" sx={{ p: 3 }}>
          <Typography fontWeight={700} color={NAVY} mb={2}>פרטי הספק</Typography>
          <Stack spacing={1.2}>
            {record.phone && (
              <Stack direction="row" spacing={1.5} alignItems="center">
                <PhoneIcon sx={{ color: GOLD, fontSize: 18 }} />
                <Typography>{record.phone}</Typography>
              </Stack>
            )}
            {record.email && (
              <Stack direction="row" spacing={1.5} alignItems="center">
                <EmailIcon sx={{ color: GOLD, fontSize: 18 }} />
                <Typography>{record.email}</Typography>
              </Stack>
            )}
            {record.notes && (
              <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <NoteIcon sx={{ color: GOLD, fontSize: 18, mt: 0.2 }} />
                <Typography color="text.secondary">{record.notes}</Typography>
              </Stack>
            )}
          </Stack>
        </CardContent>
      </Card>

      {/* Summary stats */}
      <Stack className="crm-wrap crm-stat-row" direction="row" spacing={2} mb={3} flexWrap="wrap">
        <Card sx={{ borderRadius: 2, minWidth: 140 }}>
          <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="caption" color="text.secondary">סה"כ רכישות</Typography>
            <Typography fontWeight={700} fontSize={22} color={NAVY}>{purchases.length}</Typography>
          </CardContent>
        </Card>
        <Card sx={{ borderRadius: 2, minWidth: 140 }}>
          <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="caption" color="text.secondary">פריטים שנקנו</Typography>
            <Typography fontWeight={700} fontSize={22} color={NAVY}>{totalItems}</Typography>
          </CardContent>
        </Card>
        {totalIls > 0 && (
          <Card sx={{ borderRadius: 2, minWidth: 160 }}>
            <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">סה"כ ₪</Typography>
              <Typography fontWeight={700} fontSize={20} color="#2e7d32">
                ₪{totalIls.toLocaleString('he-IL', { minimumFractionDigits: 0 })}
              </Typography>
            </CardContent>
          </Card>
        )}
        {totalUsd > 0 && (
          <Card sx={{ borderRadius: 2, minWidth: 160 }}>
            <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">סה"כ $</Typography>
              <Typography fontWeight={700} fontSize={20} color="#1565c0">
                ${totalUsd.toLocaleString('en-US', { minimumFractionDigits: 0 })}
              </Typography>
            </CardContent>
          </Card>
        )}
      </Stack>

      {/* Purchase history */}
      <Typography variant="h6" fontWeight={700} color={NAVY} mb={2}>היסטוריית רכישות</Typography>

      {isLoading ? (
        <Typography color="text.secondary">טוען...</Typography>
      ) : purchases.length === 0 ? (
        <Typography color="text.secondary">אין רכישות מספק זה עדיין</Typography>
      ) : (
        <Card sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <Box className="crm-table-scroll" data-mobile-scroll tabIndex={0} role="region" aria-label="טבלה נגללת"><Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>מוצר</TableCell>
                <TableCell align="center">כמות</TableCell>
                <TableCell align="right">מחיר ליחידה</TableCell>
                <TableCell align="right">סה"כ</TableCell>
                <TableCell align="center">תאריך</TableCell>
                <TableCell>הערות</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {purchases.map((p, i) => {
                const lineTotal = Number(p.unit_cost || 0) * Number(p.quantity || 1);
                return (
                  <TableRow key={p.id} sx={tbl.zebraRow(i)}>
                    <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: 13 }}>#{p.id}</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: 14 }}>
                      {p.variant_name || `וריאנט #${p.variant_id}`}
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>{fmtQty(p.quantity, p.unit)}</TableCell>
                    <TableCell align="right" sx={{ color: 'text.secondary' }}>
                      {fmtCost(p.unit_cost, p.currency)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: NAVY }}>
                      {fmtCost(lineTotal, p.currency)}
                    </TableCell>
                    <TableCell align="center" sx={{ fontSize: 12, color: 'text.secondary' }}>
                      {fmtDate(p.purchase_date)}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12, color: 'text.secondary', maxWidth: 200 }}>
                      {p.notes || '—'}
                    </TableCell>
                  </TableRow>
                );
              })}

              {/* Totals row */}
              <TableRow sx={{ backgroundColor: '#f0ece3' }}>
                <TableCell colSpan={2} sx={{ fontWeight: 700 }}>סה"כ</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>{totalItems}</TableCell>
                <TableCell />
                <TableCell align="right" sx={{ fontWeight: 700, color: NAVY }}>
                  <Stack alignItems="flex-end" spacing={0.3}>
                    {totalIls > 0 && <span>₪{totalIls.toLocaleString('he-IL', { minimumFractionDigits: 2 })}</span>}
                    {totalUsd > 0 && <span>${totalUsd.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>}
                  </Stack>
                </TableCell>
                <TableCell colSpan={2} />
              </TableRow>
            </TableBody>
          </Table></Box>
        </Card>
      )}
    </Box>
  );
};

const SupplierShow = () => (
  <Show title=" " actions={false}>
    <SupplierDetail />
  </Show>
);

export default SupplierShow;
